import { PROTECOES_INICIAIS } from '../didatico/manejoVidros'
import { GEOMETRIA_PLACA_ANCORAGEM, PELVE_EM_PE_M, PELVE_SENTADO_M, pontoNoMundo } from './ancoragem'
import { alinharPoliaComCorda } from './alinharPolia'
import { novoEnvolvimentoEm } from './envolvimento'
import { MEIO_MOSQUETAO_M } from './fitaDeAncoragem'
import { FUNDO_DO_CANAL_POLIA_M } from './tracadoCorda'
import { ESPESSURA_MURETA_M, medidasDoCanal } from './terreno'
import { copiarCenaTabletop } from './operacoes'
import type { FuncaoCorda } from './funcoesCorda'
import type {
  AncoragemTabletop,
  CenaTabletop,
  CordaTabletop,
  ObjetoTabletop,
  ParametrosTabletop,
  ReferenciaPonto,
  SentidoCorda,
  TipoObjetoTabletop,
  VetorTabletop,
} from './tipos'

/**
 * Presets de salvamento terrestre: as duas técnicas das fotos de referência
 * (`docs/referencias/`), montadas sobre um barranco com a viatura em cima.
 *
 * São uma INTERPRETAÇÃO das imagens, feita só com o que o table top sabe
 * representar (escada, polias, bloqueadores, mosquetões, toco, maca, viatura,
 * barranco e cordas com função). Quem confirma que a montagem bate com a foto
 * é o instrutor — ver `pontosParaValidar` de cada preset. As posições dos
 * equipamentos pendurados (polias, mosquetões, maca) são calculadas a partir
 * dos pontos da escada, então mudar uma medida aqui não desmonta a técnica.
 */

export interface PredefinicaoSalvamento {
  readonly id: string
  readonly titulo: string
  readonly descricao: string
  /** Arquivo da foto de referência, em `docs/referencias/`. */
  readonly referencia: string
  /** O que foi simplificado ou assumido e precisa do olhar do instrutor. */
  readonly pontosParaValidar: readonly string[]
  readonly criarCena: () => CenaTabletop
}

type Vetor = [number, number, number]

const BARRANCO = { alturaM: 4, anguloGraus: 75, larguraM: 14, topoM: 10, pistaNoTopo: 1 } as const
/** O topo do barranco: viatura, escada e toco assentam nesta altura. */
const ALTURA_TOPO_M = BARRANCO.alturaM

const peca = (
  tipo: TipoObjetoTabletop,
  id: string,
  nome: string,
  posicao: Vetor,
  extra: { rotacao?: Vetor; parametros?: ParametrosTabletop; cor?: string } = {},
): ObjetoTabletop => ({
  id,
  tipo,
  nome,
  transformacao: { posicao, rotacao: extra.rotacao ?? [0, 0, 0], escala: [1, 1, 1] },
  ...(extra.parametros ? { parametros: extra.parametros } : {}),
  ...(extra.cor ? { cor: extra.cor } : {}),
})

const ponto = (idObjeto: string, idPonto: string): ReferenciaPonto => ({ idObjeto, idPonto })

const corda = (
  id: string,
  funcao: FuncaoCorda,
  rotulo: string,
  percurso: readonly ReferenciaPonto[],
  sentido?: SentidoCorda,
): CordaTabletop => ({ id, funcao, rotulo, percurso, ...(sentido ? { sentido } : {}) })

const somar = (a: VetorLike, b: VetorLike): Vetor => [a[0] + b[0], a[1] + b[1], a[2] + b[2]]
type VetorLike = readonly [number, number, number]

/** O ponto no meio de dois pontos, descido `queda` metros e afastado `recuo` em Z. */
const penduradoEntre = (a: VetorLike, b: VetorLike, queda: number, recuo = 0): Vetor => [
  (a[0] + b[0]) / 2,
  (a[1] + b[1]) / 2 - queda,
  (a[2] + b[2]) / 2 + recuo,
]

/** Árvores na encosta, abaixo da borda, como na foto da estrada sobre o barranco; ficam longe da maca e da tirolesa. */
const ARVORES_DA_ENCOSTA: ReadonlyArray<readonly [number, number]> = [
  [-6, -8.5], [6.5, -9.5], [-8.5, -12.5], [8, -13], [-4.5, -15.5], [4, -16.5],
]

/**
 * Estrada no topo de um barranco, com a viatura de ré para a borda e a encosta
 * com mato e árvores embaixo: é o cenário das duas técnicas das fotos.
 */
function cenarioBase(): ObjetoTabletop[] {
  return [
    peca('barranco', 'barranco', 'Barranco (estrada no topo)', [0, 0, 0], { parametros: BARRANCO }),
    ...ARVORES_DA_ENCOSTA.map(([x, z], i) => peca('arvore', `arvore-${i + 1}`, `Árvore ${i + 1}`, [x, 0, z])),
    // a traseira da viatura (+X do modelo) aponta para a borda (-Z do mundo)
    peca('caminhaoBombeiros', 'viatura', 'Viatura (ABT)', [0, ALTURA_TOPO_M, 1.1], {
      rotacao: [0, Math.PI / 2, 0],
      cor: '#dc2626',
    }),
  ]
}

const comoPonto = (o: ObjetoTabletop, idPonto: string): Vetor => {
  const [x, y, z] = pontoNoMundo(o, idPonto)
  return [x, y, z]
}

interface OpcoesMastroDeEscada {
  /** A escada inclinada, que faz de mastro sobre a borda. */
  readonly escada: ObjetoTabletop
  /** Altura da plataforma onde ficam a viatura, os bloqueadores e o toco. */
  readonly yPlataforma: number
  /** Altura do chão sob a polia do topo, onde fica a maca (o fundo do barranco, a banqueta do canal). */
  readonly yMaca: number
  /** Posições (X, Z) dos dois bloqueadores e do toco de ancoragem, na plataforma. */
  readonly bloqueador1: readonly [number, number]
  readonly bloqueador2: readonly [number, number]
  readonly toco: readonly [number, number]
  /** Giro da maca em Y, para ela caber na banqueta. */
  readonly rotacaoMaca?: Vetor
  readonly idViatura: string
  /**
   * Um sistema de vantagem mecânica no lugar dos dois bloqueadores: o bloco fixo
   * fica em `posicao`, preso ao toco, e o eixo do sistema aponta para o lado
   * dado por `giroY` (0 aponta para +X do mundo; π para -X).
   */
  readonly sistema?: { readonly razao: number; readonly cursoM: number; readonly posicao: readonly [number, number]; readonly giroY: number }
}

/**
 * A técnica do mastro: a escada inclinada sobre a borda, com a polia do topo
 * sobre a maca, duas fitas em V, duas estabilizações até a viatura e o
 * içamento por polias e bloqueadores ancorados num toco. Tudo que pende é
 * calculado a partir dos pontos da escada.
 */
function montarMastroDeEscada(o: OpcoesMastroDeEscada): { objetos: ObjetoTabletop[]; cordas: CordaTabletop[] } {
  const { escada, yPlataforma, yMaca } = o
  // fita em V do topo: duas pernas na escada e a polia no vértice, abaixo delas
  const apexTopo = penduradoEntre(comoPonto(escada, 'degrau-17'), comoPonto(escada, 'topo'), 0.7)
  const poliaTopo = peca('polia', 'polia-topo', 'Polia do topo', somar(apexTopo, [0, -0.075, 0]))

  // fita em V do meio: o vértice é um mosquetão
  const apexMeio = penduradoEntre(comoPonto(escada, 'degrau-8'), comoPonto(escada, 'degrau-12'), 0.6)
  const mosquetaoMeio = peca('mosquetao', 'mosquetao-meio', 'Mosquetão do meio', apexMeio)

  // a maca pende sob a polia do topo, ligada por mosquetão
  const olhalMaca: Vetor = [apexTopo[0], yMaca + 0.75, apexTopo[2]]
  const maca = peca('maca', 'maca', 'Maca', [apexTopo[0], yMaca, apexTopo[2]], { ...(o.rotacaoMaca ? { rotacao: o.rotacaoMaca } : {}) })
  const mosquetaoMaca = peca('mosquetao', 'mosquetao-maca', 'Mosquetão da maca', somar(olhalMaca, [0, 0.12, 0]))
  // o nó oito fica logo acima do mosquetão, como na foto (o laço do oito passa pelo mosquetão)
  const noOito = peca('noOito', 'no-oito', 'Nó oito', somar(olhalMaca, [0, 0.32, 0]))

  // polia no pé da escada e o sistema de vantagem mecânica ancorado no toco
  const poliaPe = peca('polia', 'polia-pe', 'Polia do pé', somar(comoPonto(escada, 'pe'), [0, 0.15, 0]))
  const toco = peca('toco', 'toco', 'Toco de ancoragem', [o.toco[0], yPlataforma, o.toco[1]])
  const bloqueador1 = peca('bloqueador', 'bloqueador-1', 'Bloqueador 1', [o.bloqueador1[0], yPlataforma + 0.2, o.bloqueador1[1]])
  const bloqueador2 = peca('bloqueador', 'bloqueador-2', 'Bloqueador 2', [o.bloqueador2[0], yPlataforma + 0.2, o.bloqueador2[1]])
  const sistema = o.sistema
    ? peca('sistemaVM', 'sistema-vm', `Sistema ${o.sistema.razao}:1`, [o.sistema.posicao[0], yPlataforma + 0.2, o.sistema.posicao[1]], {
        rotacao: [0, o.sistema.giroY, 0],
        parametros: { razao: o.sistema.razao, cursoM: o.sistema.cursoM },
      })
    : null

  return {
    objetos: [poliaTopo, mosquetaoMeio, maca, mosquetaoMaca, noOito, poliaPe, ...(sistema ? [sistema] : [bloqueador1, bloqueador2]), toco],
    cordas: [
      corda('fita-v-topo', 'fita', 'Fita em V do topo', [ponto('escada', 'degrau-17'), ponto('polia-topo', 'olhal'), ponto('escada', 'topo')]),
      corda('fita-v-meio', 'fita', 'Fita em V do meio', [ponto('escada', 'degrau-8'), ponto('mosquetao-meio', 'passagem'), ponto('escada', 'degrau-12')]),
      corda('estabilizacao-topo', 'estabilizacao', 'Estabilização do topo', [ponto('polia-topo', 'olhal'), ponto(o.idViatura, 'topo-traseiro-esq')]),
      corda('estabilizacao-meio', 'estabilizacao', 'Estabilização do meio', [ponto('mosquetao-meio', 'passagem'), ponto(o.idViatura, 'topo-traseiro-dir')]),
      corda(
        'icamento',
        'tracao',
        'Içamento',
        [ponto('maca', 'olhal'), ponto('mosquetao-maca', 'passagem'), ponto('no-oito', 'passagem'), ponto('polia-topo', 'entrada'), ponto('polia-pe', 'entrada'), sistema ? ponto('sistema-vm', 'carga') : ponto('bloqueador-1', 'entrada')],
      ),
      corda('fita-toco', 'fita', 'Fita do toco', [ponto('toco', 'base'), sistema ? ponto('sistema-vm', 'ancoragem') : ponto('bloqueador-1', 'olhal')]),
      // com o sistema de vantagem, a puxada está dentro dele (a seta grande na ponta livre); com os bloqueadores é uma corda à parte
      ...(sistema ? [] : [corda('vantagem-mecanica', 'tracao', 'Puxada', [ponto('bloqueador-1', 'saida'), ponto('bloqueador-2', 'entrada')], 'paraFim')]),
    ],
  }
}

/** Na foto a maca sobe pendurada (seta para cima): fica no ar, acima do fundo do barranco. */
const ALTURA_MACA_ICADA_M = 1.5

function criarEscada45(): CenaTabletop {
  const escada = peca('escada', 'escada', 'Escada 45°', [0, ALTURA_TOPO_M, -3], {
    rotacao: [0, Math.PI, 0], // sobe para -Z, por cima da borda
    parametros: { anguloGraus: 45, comprimentoM: 6 },
  })
  const mastro = montarMastroDeEscada({
    escada,
    idViatura: 'viatura',
    yPlataforma: ALTURA_TOPO_M,
    // a maca está sendo içada: no ar, 1,5 m acima do fundo, como na foto
    yMaca: ALTURA_MACA_ICADA_M,
    bloqueador1: [2.2, -2.6],
    bloqueador2: [3.6, -2.6],
    toco: [5.4, -2.6],
    // o içamento desce da polia do pé para um sistema 3:1 (polias e bloqueador) ancorado no toco
    sistema: { razao: 3, cursoM: 2.4, posicao: [4.2, -2.6], giroY: Math.PI },
  })

  return {
    versao: 2,
    nome: 'Técnica escada 45° sobre o barranco',
    historiaInicial:
      'Vítima no fundo de um barranco, abaixo da estrada. A escada, inclinada a 45° sobre a borda, faz de mastro: a polia do topo fica sobre a maca, que sobe pendurada, e a viatura segura a escada por duas cordas de estabilização. O içamento desce pela polia do pé até um sistema 3:1 ancorado num toco.',
    chao: 'mato',
    objetos: [...cenarioBase(), escada, ...mastro.objetos],
    cordas: mastro.cordas,
  }
}

/** Do centro da polia ao olhal dela. */
const OLHAL_DA_POLIA_M = 0.075
/** O mosquetão inteiro: do olhal da polia, embaixo, ao vértice das fitas, em cima. */
const COMPRIMENTO_DO_MOSQUETAO_M = 2 * MEIO_MOSQUETAO_M
/** Quanto a tirolesa guia cede no carrinho com a maca pendurada. */
const FLECHA_DA_GUIA_M = 0.25

const escalar = (a: VetorLike, k: number): Vetor => [a[0] * k, a[1] * k, a[2] * k]

/**
 * Para onde aponta o olhal de uma polia em `centro` com a corda indo para `a`
 * e para `b`: contra a resultante das pernas (é a mesma conta de `alinharPolia`).
 */
function eixoDoOlhalNaCorda(centro: VetorLike, a: VetorLike, b: VetorLike): Vetor {
  const u = (p: VetorLike): Vetor => {
    const d: Vetor = [p[0] - centro[0], p[1] - centro[1], p[2] - centro[2]]
    const n = Math.hypot(d[0], d[1], d[2])
    return [d[0] / n, d[1] / n, d[2] / n]
  }
  const r = somar(u(a), u(b))
  const n = Math.hypot(r[0], r[1], r[2])
  return n > 1e-9 ? [-r[0] / n, -r[1] / n, -r[2] / n] : [0, 1, 0]
}

function criarTirolesaInvertida(): CenaTabletop {
  const escada = peca('escada', 'escada', 'Escada vertical', [0, ALTURA_TOPO_M, -2.6], {
    parametros: { anguloGraus: 90, comprimentoM: 6 },
  })
  const topoEscada = comoPonto(escada, 'topo')

  // o V da tirolesa guia, no alto da escada: um anel de fita em cada banzo do degrau 18,
  // unidos pelo mosquetão, que fica 35 cm abaixo e 25 cm à frente (para o lado da guia)
  const VERTICE_DO_V: Vetor = [0, -0.35, -0.25]
  const apexGuia = somar(comoPonto(escada, 'degrau-18'), VERTICE_DO_V)
  const mosquetaoGuia = peca('mosquetao', 'mosquetao-guia', 'Mosquetão da guia', apexGuia)

  // o V da polia do içamento, mais abaixo, no degrau 12: as fitas clipam no mosquetão e a polia pende dele pelo olhal
  const apexIcamento = somar(comoPonto(escada, 'degrau-12'), VERTICE_DO_V)

  // a tirolesa desce da escada até um toco no fundo; o carrinho (polia) corre sobre ela
  const tocoFundo = peca('toco', 'toco-fundo', 'Toco do fundo', [0, 0, -12])
  const fim = comoPonto(tocoFundo, 'base')
  const t = 0.4
  // com a maca pendurada, a guia cede um pouco no carrinho: a corda passa por baixo da roldana
  const naGuia: Vetor = [
    apexGuia[0] + (fim[0] - apexGuia[0]) * t,
    apexGuia[1] + (fim[1] - apexGuia[1]) * t - FLECHA_DA_GUIA_M,
    apexGuia[2] + (fim[2] - apexGuia[2]) * t,
  ]
  const centroCarrinho = somar(naGuia, [0, FUNDO_DO_CANAL_POLIA_M, 0])
  const carrinho = peca('polia', 'polia-carrinho', 'Carrinho da tirolesa', centroCarrinho)
  // o olhal do carrinho aponta contra as pernas da guia: para baixo, para a maca
  const olhalCarrinho = somar(centroCarrinho, escalar(eixoDoOlhalNaCorda(centroCarrinho, apexGuia, fim), OLHAL_DA_POLIA_M))
  // a maca pende 60 cm sob o carrinho; o olhal dela fica 75 cm acima da origem
  const maca = peca('maca', 'maca', 'Maca', [olhalCarrinho[0], olhalCarrinho[1] - 0.6 - 0.75, olhalCarrinho[2]])

  const sistema = peca('sistemaVM', 'sistema-vm', 'Sistema 3:1', [4.2, ALTURA_TOPO_M + 0.2, -2], {
    rotacao: [0, Math.PI, 0],
    parametros: { razao: 3, cursoM: 2.4 },
  })
  const cargaDoSistema = comoPonto(sistema, 'carga')

  // o içamento sai da maca por um nó oito, passa pela roldana da polia da escada e segue para o sistema.
  // A polia pende do mosquetão do V: olhal, mosquetão e vértice das fitas ficam na linha da resultante
  // das duas pernas do içamento (é para onde a carga puxa a polia).
  const noOitoPara = (centroPolia: VetorLike): Vetor => [
    olhalCarrinho[0] + (centroPolia[0] - olhalCarrinho[0]) * 0.08,
    olhalCarrinho[1] + (centroPolia[1] - olhalCarrinho[1]) * 0.08,
    olhalCarrinho[2] + (centroPolia[2] - olhalCarrinho[2]) * 0.08,
  ]
  let centroIcamento: Vetor = somar(apexIcamento, [0, -(OLHAL_DA_POLIA_M + COMPRIMENTO_DO_MOSQUETAO_M), 0])
  for (let k = 0; k < 6; k += 1) {
    const eixo = eixoDoOlhalNaCorda(centroIcamento, noOitoPara(centroIcamento), cargaDoSistema)
    centroIcamento = somar(apexIcamento, escalar(eixo, -(OLHAL_DA_POLIA_M + COMPRIMENTO_DO_MOSQUETAO_M)))
  }
  const poliaIcamento = peca('polia', 'polia-icamento', 'Polia do içamento', centroIcamento)
  const noOito = peca('noOito', 'no-oito', 'Nó oito', noOitoPara(centroIcamento))
  const tocoAncora = peca('toco', 'toco', 'Toco de ancoragem', [5.4, ALTURA_TOPO_M, -2])

  const cena: CenaTabletop = {
    versao: 2,
    nome: 'Técnica tirolesa invertida',
    historiaInicial:
      'Escada vertical encostada na traseira da viatura, na estrada sobre o barranco. A maca corre pela tirolesa guia, do alto da escada até o fundo, e é puxada pelo sistema de içamento, que passa pela polia da escada e vai a um sistema 3:1 ancorado num toco. Na escada, cada ancoragem é um V de dois anéis de fita, um em cada banzo, unidos por um mosquetão: o de cima segura a guia e a estabilização do topo, que puxam para baixo dos dois lados e deixam a escada em compressão; o de baixo segura a polia do içamento. Mais duas estabilizações, no meio e no pé, prendem a escada na viatura.',
    chao: 'mato',
    objetos: [...cenarioBase(), escada, mosquetaoGuia, poliaIcamento, tocoFundo, carrinho, maca, noOito, sistema, tocoAncora],
    cordas: [
      // a guia passa pela roldana do carrinho (entrada e saída), e o içamento pela da polia da escada
      corda('tirolesa-guia', 'tirolesa', 'Tirolesa guia', [
        ponto('mosquetao-guia', 'passagem'),
        ponto('polia-carrinho', 'entrada'),
        ponto('polia-carrinho', 'saida'),
        ponto('toco-fundo', 'base'),
      ]),
      corda('linha-maca', 'vitima', 'Maca no carrinho', [ponto('polia-carrinho', 'olhal'), ponto('maca', 'olhal')]),
      corda('icamento', 'tracao', 'Içamento', [
        ponto('polia-carrinho', 'olhal'),
        ponto('no-oito', 'passagem'),
        ponto('polia-icamento', 'entrada'),
        ponto('polia-icamento', 'saida'),
        ponto('sistema-vm', 'carga'),
      ]),
      corda('fita-toco', 'fita', 'Fita do toco', [ponto('toco', 'base'), ponto('sistema-vm', 'ancoragem')]),
      // a estabilização do topo sai do mesmo mosquetão da guia, para o outro lado: as duas puxam para baixo
      corda('estabilizacao-topo', 'estabilizacao', 'Estabilização do topo', [ponto('mosquetao-guia', 'passagem'), ponto('viatura', 'topo-traseiro-esq')]),
      corda('estabilizacao-meio', 'estabilizacao', 'Estabilização horizontal do meio', [ponto('escada', 'degrau-10'), ponto('viatura', 'topo-traseiro-dir')]),
      corda('estabilizacao-base', 'estabilizacao', 'Estabilização da base', [ponto('escada', 'pe'), ponto('viatura', 'degrau-traseiro')]),
    ],
    // as duas ancoragens na escada: dois anéis de fita, um em cada banzo, unidos pelo mosquetão
    ancoragens: [
      {
        id: 'v-guia',
        tipo: 'V',
        fixacoes: [ponto('escada', 'banzo-esq-18'), ponto('escada', 'banzo-dir-18')],
        centro: ponto('mosquetao-guia', 'passagem'),
        cargaKgf: 200,
        rotulo: 'V de anéis da guia',
        equalizacao: 'pre-equalizada',
      },
      {
        id: 'v-icamento',
        tipo: 'V',
        fixacoes: [ponto('escada', 'banzo-esq-12'), ponto('escada', 'banzo-dir-12')],
        centro: ponto('polia-icamento', 'olhal'),
        cargaKgf: 200,
        rotulo: 'V de anéis do içamento',
        equalizacao: 'pre-equalizada',
      },
    ],
  }
  // cada polia se acomoda na corda que passa pela roldana dela: a roldana no plano das pernas
  const alinhada = alinharPoliaComCorda(alinharPoliaComCorda(cena, 'polia-carrinho'), 'polia-icamento')
  // sem −0 nos ângulos: a cena salva e aberta volta igual
  return {
    ...alinhada,
    objetos: alinhada.objetos.map((o) => ({
      ...o,
      transformacao: { ...o.transformacao, rotacao: o.transformacao.rotacao.map((r) => r + 0) as Vetor },
    })),
  }
}

// ---------------------------------------------------------------------------
// Skate block (teleférico)
// ---------------------------------------------------------------------------

/**
 * A técnica skate block: uma corda só faz a guia e o retorno. Sai do operador,
 * lá embaixo, passa pela polia do pé, sobe pela guia (onde o skate block corre),
 * contorna a polia do topo e desce de volta até o nó oito no mosquetão do skate
 * block, que carrega a maca. Puxar a ponta livre sobe a maca; soltar, desce.
 */
function criarSkateBlock(): CenaTabletop {
  // o poste do topo é a ancoragem alta: a polia pende dele, sobre o vazio, e a equipe trabalha na borda
  const poste = peca('poste', 'poste-topo', 'Poste de ancoragem (topo)', [0, ALTURA_TOPO_M, -3.7])
  const meioDoPoste = comoPonto(poste, 'meio')
  // fita do poste → mosquetão → olhal da polia: o mosquetão fica no vértice, a polia pende abaixo e para o lado do vazio
  const apexTopo = somar(meioDoPoste, [0, -0.35, -0.3])
  const mosquetaoTopo = peca('mosquetao', 'mosquetao-topo', 'Mosquetão do topo', apexTopo)
  const centroTopo = somar(apexTopo, [0, -(OLHAL_DA_POLIA_M + COMPRIMENTO_DO_MOSQUETAO_M), 0])
  const poliaTopo = peca('polia', 'polia-topo', 'Polia do topo', centroTopo)

  // lá embaixo: um toco no fundo e a polia do pé, presa a ele por fita, puxada para o lado da guia
  const tocoFundo = peca('toco', 'toco-fundo', 'Toco do fundo', [0, 0, -9.6])
  const baseFundo = comoPonto(tocoFundo, 'base')
  const centroFundo = somar(baseFundo, [0, 0.1, 0.45])
  const poliaFundo = peca('polia', 'polia-fundo', 'Polia do pé', centroFundo)

  // o skate block corre na guia, entre as duas polias; com a maca pendurada a guia cede um pouco
  const saidaTopo = somar(centroTopo, [0.035, -0.02, 0])
  const entradaFundo = somar(centroFundo, [-0.035, -0.02, 0])
  const t = 0.42
  const naGuia: Vetor = [
    saidaTopo[0] + (entradaFundo[0] - saidaTopo[0]) * t,
    saidaTopo[1] + (entradaFundo[1] - saidaTopo[1]) * t - 0.15,
    saidaTopo[2] + (entradaFundo[2] - saidaTopo[2]) * t,
  ]
  const centroSkate = somar(naGuia, [0, FUNDO_DO_CANAL_POLIA_M, 0])
  const skate = peca('polia', 'skate-block', 'Skate block', centroSkate)
  const olhalSkate = somar(centroSkate, escalar(eixoDoOlhalNaCorda(centroSkate, centroTopo, centroFundo), OLHAL_DA_POLIA_M))
  // o mosquetão giratório pende do olhal; o nó oito da corda de retorno fecha nele, pelo lado do topo
  const mosquetaoSkate = peca('mosquetao', 'mosquetao-skate', 'Mosquetão do skate block', somar(olhalSkate, [0, -0.1, 0]))
  const noOito = peca('noOito', 'no-oito', 'Nó oito', somar(olhalSkate, [0, -0.04, 0.16]))
  // a maca pende 60 cm sob o skate block, pelo mosquetão (o olhal dela fica 75 cm acima da origem)
  const olhalMaca: Vetor = somar(mosquetaoSkate.transformacao.posicao as VetorLike, [0, -0.12, 0])
  const maca = peca('maca', 'maca', 'Maca', [olhalMaca[0], olhalMaca[1] - 0.75, olhalMaca[2]], { rotacao: [0, Math.PI / 2, 0] })

  // a equipe: um bombeiro na borda, junto do poste, e o operador no fundo, segurando a ponta livre
  const bombeiroTopo = peca('bombeiro', 'bombeiro-topo', 'Bombeiro da borda', [-1.1, ALTURA_TOPO_M, -3.7], { rotacao: [0, Math.PI, 0] })
  const operador = peca('bombeiro', 'operador-fundo', 'Operador da ponta livre', [0.7, 0, -9.7], { rotacao: [0, Math.PI, 0] })

  const cena: CenaTabletop = {
    versao: 2,
    nome: 'Técnica skate block (teleférico)',
    historiaInicial:
      'Vítima na maca, na borda do barranco, e a equipe embaixo. Uma corda só faz a guia e o retorno: passa pela polia do pé, sobe pela guia (onde o skate block corre), contorna a polia do topo, presa ao poste por fita e mosquetão, e volta até o nó oito no mosquetão do skate block, que carrega a maca. O operador do fundo puxa a ponta livre para subir a maca e a solta aos poucos para descer.',
    chao: 'mato',
    objetos: [
      ...cenarioBase(),
      poste,
      mosquetaoTopo,
      poliaTopo,
      tocoFundo,
      poliaFundo,
      skate,
      mosquetaoSkate,
      noOito,
      maca,
      bombeiroTopo,
      operador,
    ],
    cordas: [
      corda('fita-topo', 'fita', 'Fita do topo', [ponto('poste-topo', 'meio'), ponto('mosquetao-topo', 'passagem'), ponto('polia-topo', 'olhal')]),
      corda('fita-fundo', 'fita', 'Fita do pé', [ponto('toco-fundo', 'base'), ponto('polia-fundo', 'olhal')]),
      // a mesma corda: retorno até o nó oito, polia do topo, guia pelo skate block e polia do pé até a ponta livre
      corda(
        'corda-skate-block',
        'tracao',
        'Corda do skate block (guia e retorno)',
        [
          ponto('mosquetao-skate', 'passagem'),
          ponto('no-oito', 'passagem'),
          ponto('polia-topo', 'entrada'),
          ponto('polia-topo', 'saida'),
          ponto('skate-block', 'entrada'),
          ponto('skate-block', 'saida'),
          ponto('polia-fundo', 'entrada'),
          ponto('polia-fundo', 'saida'),
        ],
        'paraFim',
      ),
      corda('linha-maca', 'vitima', 'Maca no skate block', [ponto('skate-block', 'olhal'), ponto('mosquetao-skate', 'passagem'), ponto('maca', 'olhal')]),
    ],
  }

  // cada polia se acomoda na corda que passa pela roldana dela; a do pé não tem ponto depois da saída,
  // então se alinha com um ponto de apoio provisório no toco (o plano é o mesmo da guia)
  const comApoio: CenaTabletop = {
    ...cena,
    cordas: cena.cordas!.map((c) =>
      c.id === 'corda-skate-block' ? { ...c, percurso: [...c.percurso, ponto('toco-fundo', 'base')] } : c,
    ),
  }
  const alinhada = ['polia-topo', 'skate-block', 'polia-fundo'].reduce((c, id) => alinharPoliaComCorda(c, id), comApoio)
  const rotacoes = new Map(alinhada.objetos.map((o) => [o.id, o.transformacao.rotacao]))
  return {
    ...cena,
    // sem −0 nos ângulos: a cena salva e aberta volta igual
    objetos: cena.objetos.map((o) => ({
      ...o,
      transformacao: { ...o.transformacao, rotacao: (rotacoes.get(o.id) ?? o.transformacao.rotacao).map((r) => r + 0) as Vetor },
    })),
  }
}

// ---------------------------------------------------------------------------
// Canal e ponte (referência: Ribeirão Arrudas, em Belo Horizonte)
// ---------------------------------------------------------------------------

/**
 * Medidas didáticas do canal. As fotos não dão as medidas reais (altura das
 * paredes, largura do leito, banqueta, vão das pontes): o instrutor ajusta
 * todas no inspetor do canal.
 */
const CANAL = {
  comprimentoM: 40,
  larguraLeitoM: 10,
  alturaParedeM: 5,
  banquetaLarguraM: 1.5,
  banquetaAlturaM: 1.2,
  inclinacaoMargemGraus: 90,
  muretaAlturaM: 1,
  nivelAguaM: 0.5,
  correnteza: 1,
} as const

function criarCanalEPonte(): CenaTabletop {
  const m = medidasDoCanal({ ...CANAL })
  // a rua é o chão da cena, no nível 0: o canal é escavado abaixo dela
  const rua = 0
  // a viatura estaciona rente à mureta, do lado da banqueta; a traseira (+X do modelo) fica para a escada
  const zViatura = m.paredeZ - ESPESSURA_MURETA_M - 0.4 - 0.95
  const viatura = peca('caminhaoBombeiros', 'viatura', 'Viatura (ABT)', [-4, rua, zViatura], { cor: '#dc2626' })
  // escada a 45° por cima da mureta: o pé na rua e o topo sobre a banqueta (3 m abaixo)
  const alcance = 6 * Math.cos(Math.PI / 4)
  const zBanqueta = -(m.meioLeitoM + m.banquetaM / 2)
  const escada = peca('escada', 'escada', 'Escada 45°', [0.3, rua, zBanqueta - alcance], {
    parametros: { anguloGraus: 45, comprimentoM: 6 },
  })
  const mastro = montarMastroDeEscada({
    escada,
    idViatura: 'viatura',
    yPlataforma: rua,
    yMaca: m.yBanquetaM,
    bloqueador1: [3, zViatura],
    bloqueador2: [4.4, zViatura],
    toco: [6.4, zViatura],
    // 3:1 em Z: o bloco fixo junto do toco, o móvel voltado para a escada
    sistema: { razao: 3, cursoM: 2.4, posicao: [5.2, zViatura], giroY: Math.PI },
    // a maca é comprida: deitada ao longo do canal para caber na banqueta de 1,5 m
    rotacaoMaca: [0, Math.PI / 2, 0],
  })
  const maca = mastro.objetos.find((o) => o.id === 'maca')!
  const [xMaca, , zMaca] = maca.transformacao.posicao
  const vitima = peca('vitima', 'vitima', 'Vítima', [xMaca, m.yBanquetaM + 0.12, zMaca], { rotacao: [0, 0, Math.PI / 2] })
  // a equipe, como na foto: dois bombeiros na banqueta, um em cada ponta da maca, e um na rua junto da escada
  const peEscada = comoPonto(escada, 'pe')
  const bombeiros = [
    peca('bombeiro', 'bombeiro-1', 'Bombeiro 1 (banqueta)', [xMaca - 1.6, m.yBanquetaM, zMaca], { rotacao: [0, Math.PI / 2, 0] }),
    peca('bombeiro', 'bombeiro-2', 'Bombeiro 2 (banqueta)', [xMaca + 1.6, m.yBanquetaM, zMaca], { rotacao: [0, -Math.PI / 2, 0] }),
    peca('bombeiro', 'bombeiro-3', 'Bombeiro 3 (rua)', [peEscada[0] + 1.3, rua, peEscada[2] - 0.6]),
  ]
  // a viatura de resgate estaciona atrás da ABT, também rente à mureta
  const resgate = peca('resgate', 'viatura-resgate', 'Viatura de resgate', [-13, rua, zViatura], { cor: '#dc2626' })

  // duas pontes baixas em sequência, sem pilares no vão; o tabuleiro fica à altura da rua
  const zMeioDoVao = (m.paredeZ + m.topoDireitoZ) / 2
  const vao = m.topoDireitoZ - m.paredeZ
  const ponte1 = peca('ponte', 'ponte-1', 'Ponte 1', [8, rua, zMeioDoVao], { parametros: { vaoM: vao } })
  const ponte2 = peca('ponte', 'ponte-2', 'Ponte 2', [16.5, rua, zMeioDoVao], { parametros: { vaoM: vao } })
  // carro capotado na água, sob a primeira ponte
  const carro: ObjetoTabletop = {
    ...peca('carro', 'carro-capotado', 'Carro capotado', [8, m.yLeitoM + 0.9, 0.5], { rotacao: [Math.PI, 0.25, 0], cor: '#374151' }),
    veiculo: {
      idPerfil: 'hatchCompacto',
      posicaoFinal: 'capotado',
      estadosPecas: {},
      vidros: { parabrisa: 'quebrado' },
      insulfilm: false,
      protecoes: { ...PROTECOES_INICIAIS },
    },
  }

  return {
    versao: 2,
    nome: 'Canal e ponte (tipo Arrudas)',
    historiaInicial:
      'Carro capotado na água de um canal de concreto, sob uma ponte baixa. A vítima já está na banqueta, na maca. A viatura está rente à mureta e a escada, inclinada sobre a borda, faz de mastro para içar a maca até a rua.',
    objetos: [
      peca('canal', 'canal', 'Canal em concreto', [0, 0, 0], { parametros: CANAL }),
      ponte1,
      ponte2,
      viatura,
      escada,
      ...mastro.objetos,
      vitima,
      ...bombeiros,
      resgate,
      carro,
    ],
    cordas: mastro.cordas,
  }
}

// ---------------------------------------------------------------------------
// Cisterna
// ---------------------------------------------------------------------------

function criarCisterna(): CenaTabletop {
  const profundidade = 6
  // a rua é o chão (nível 0) e a cisterna é um poço escavado: o fundo fica `profundidade` abaixo
  const fundo = -profundidade
  // em corte (metade aberta): girada 45°, a vala de inspeção fica de frente para a câmera inicial, que olha de +X +Z
  const cisterna = peca('cisterna', 'cisterna', 'Cisterna', [0, 0, 0], {
    rotacao: [0, Math.PI / 4, 0],
    parametros: { diametroM: 2.4, profundidadeM: profundidade },
  })
  // a polia fica pendurada na boca, por uma ancoragem em V ligada a dois tocos
  const poliaBoca = peca('polia', 'polia-boca', 'Polia da boca', [0, 0.15, -1])
  const toco1 = peca('toco', 'toco-1', 'Toco 1 (ancoragem da polia)', [-1.8, 0, -3.6])
  const toco2 = peca('toco', 'toco-2', 'Toco 2 (ancoragem da polia)', [1.8, 0, -3.6])
  // a vítima, na maca, no fundo
  const maca = peca('maca', 'maca', 'Maca', [0, fundo, -0.2], { rotacao: [0, Math.PI / 2, 0] })
  const vitima = peca('vitima', 'vitima', 'Vítima', [0, fundo + 0.12, -0.2], { rotacao: [0, 0, Math.PI / 2] })
  const mosquetaoMaca = peca('mosquetao', 'mosquetao-maca', 'Mosquetão da maca', [0, fundo + 0.87, -0.2])
  // sistema de vantagem mecânica 5:1 e a corda de segurança, do outro lado da boca: o bloco fixo junto do toco, o móvel voltado para a polia
  const sistema = peca('sistemaVM', 'sistema-vm', 'Sistema 5:1', [4.8, 0.2, 0.5], {
    rotacao: [0, Math.PI, 0],
    parametros: { razao: 5, cursoM: 2.4 },
  })
  const toco3 = peca('toco', 'toco-3', 'Toco 3 (ancoragem do sistema)', [6.2, 0, 0.5])
  const toco4 = peca('toco', 'toco-4', 'Toco 4 (ancoragem da segurança)', [3, 0, -2.8])

  const ancoragens: AncoragemTabletop[] = [
    {
      id: 'v-polia',
      tipo: 'V',
      fixacoes: [ponto('toco-1', 'base'), ponto('toco-2', 'base')],
      centro: ponto('polia-boca', 'olhal'),
      cargaKgf: 200,
      rotulo: 'V da polia da boca',
    },
  ]

  return {
    versao: 2,
    nome: 'Cisterna: resgate vertical',
    historiaInicial:
      'Vítima no fundo de uma cisterna de 6 m. A polia fica pendurada na boca por uma ancoragem em V, o içamento segue para um sistema de vantagem mecânica ancorado num toco e uma corda de segurança desce até a maca pela borda.',
    objetos: [cisterna, poliaBoca, toco1, toco2, maca, vitima, mosquetaoMaca, sistema, toco3, toco4],
    cordas: [
      corda(
        'icamento',
        'tracao',
        'Içamento',
        [ponto('maca', 'olhal'), ponto('mosquetao-maca', 'passagem'), ponto('polia-boca', 'entrada'), ponto('sistema-vm', 'carga')],
      ),
      corda('fita-toco', 'fita', 'Fita do toco', [ponto('toco-3', 'base'), ponto('sistema-vm', 'ancoragem')]),
      corda('seguranca', 'seguranca', 'Corda de segurança', [ponto('maca', 'alca-cabeca'), ponto('cisterna', 'borda'), ponto('toco-4', 'base')]),
    ],
    ancoragens,
  }
}

/** A laje do exemplo de altura: um barranco de parede vertical, de 4,8 m, com o topo plano de 10 m. */
const LAJE = { alturaM: 4.8, anguloGraus: 90, larguraM: 14, topoM: 10 } as const

/**
 * Dois trabalhadores na borda de uma laje de 4,8 m, presos pelo talabarte a
 * ancoragens diferentes: A numa torre, acima do engate (fator de queda baixo,
 * a queda cabe); B num toco, no nível dos pés (fator alto, a queda não cabe).
 */
function criarTrabalhoEmAltura(): CenaTabletop {
  const y = LAJE.alturaM
  const virado = [0, Math.PI, 0] as Vetor // de frente para a borda, de costas para a ancoragem
  const objetos = [
    peca('barranco', 'laje', 'Laje de 4,8 m', [0, 0, 0], { parametros: LAJE }),
    peca('torre', 'torre', 'Torre de ancoragem', [-2, y, -3.7], { parametros: { alturaM: 2.5, larguraM: 0.6 } }),
    peca('trabalhador', 'trabalhador-a', 'Trabalhador A', [-2, y, -4.5], { rotacao: virado }),
    peca('toco', 'toco', 'Toco de ancoragem', [2, y, -3.9]),
    peca('trabalhador', 'trabalhador-b', 'Trabalhador B', [2, y, -4.6], { rotacao: virado }),
  ]
  const cordas = [
    corda('talabarte-a', 'talabarte', 'Talabarte A (ancoragem acima do engate)', [
      ponto('trabalhador-a', 'engate-dorsal'),
      ponto('torre', 'topo'),
    ]),
    corda('talabarte-b', 'talabarte', 'Talabarte B (ancoragem nos pés)', [
      ponto('trabalhador-b', 'engate-dorsal'),
      ponto('toco', 'base'),
    ]),
  ]
  return {
    versao: 2,
    nome: 'Trabalho em altura: fator de queda',
    historiaInicial:
      'Dois trabalhadores na borda de uma laje de 4,8 m. O A está preso a uma torre, acima do engate; o B, a um toco no nível dos pés. Compare o fator de queda e a zona livre de queda de cada um e use "Simular a queda" no inspetor para ver o balanço contra a parede.',
    objetos,
    cordas,
  }
}

/**
 * Dois trabalhadores no patamar de 6 m de uma torre de 12 m × 3 m. A, preso no
 * canto oposto ao alçapão, só cai pela lateral, para o chão: a queda cabe. B,
 * preso junto do alçapão, com talabarte de 2 m e absorvedor de 1,75 m, não cabe
 * pela zona livre (o pior caso, com o absorvedor aberto todo) no espaço até o
 * patamar de 3 m. Na queda calculada, a queda livre é curta e o absorvedor abre
 * pouco: ele para acima do patamar. Com mais massa ou freio mais fraco, bate.
 */
function criarTrabalhoDentroDaTorre(): CenaTabletop {
  const patamar = 6
  return {
    versao: 2,
    nome: 'Trabalho dentro da torre: o patamar de baixo',
    historiaInicial:
      'Dois trabalhadores no patamar de 6 m de uma torre de treliça. O A está preso no canto oposto ao alçapão; o B, junto do alçapão, com um talabarte longo e um absorvedor grande. Dentro da torre, o patamar de baixo é o obstáculo: compare a zona livre de queda (o pior caso) com a queda simulada de cada um, e veja o que muda no B ao aumentar a massa ou diminuir a força do absorvedor.',
    objetos: [
      peca('torre', 'torre', 'Torre', [0, 0, 0], { parametros: { alturaM: 12, larguraM: 3, espacoPatamarM: 3 } }),
      // o patamar de 6 m tem o alçapão no canto (+X, -Z); A fica no canto oposto, de costas para a ancoragem dele
      peca('trabalhador', 'trabalhador-a', 'Trabalhador A', [-0.6, patamar, 0.6], { rotacao: [0, Math.PI, 0] }),
      // B fica no piso, ao lado do alçapão
      peca('trabalhador', 'trabalhador-b', 'Trabalhador B (talabarte longo)', [0.35, patamar, -0.45], {
        parametros: { comprimentoTalabarteM: 2, absorvedorAberturaM: 1.75 },
      }),
    ],
    cordas: [
      corda('talabarte-a', 'talabarte', 'Talabarte A (canto oposto ao alçapão)', [ponto('trabalhador-a', 'engate-dorsal'), ponto('torre', 'patamar-2')]),
      corda('talabarte-b', 'talabarte', 'Talabarte B (junto do alçapão)', [ponto('trabalhador-b', 'engate-dorsal'), ponto('torre', 'patamar-2-alcapao')]),
    ],
  }
}

/** A parede vertical da ancoragem básica: um barranco a 90°, girado para a face olhar para +Z (para a câmera). */
const PAREDE_ANCORAGEM = { alturaM: 8, anguloGraus: 90, larguraM: 8, topoM: 3, pistaNoTopo: 0 } as const

/**
 * A ancoragem básica da figura de referência, numa parede vertical de 8 m: a
 * corda nasce no ponto secundário, desce em catenária (frouxa) até o ponto
 * primário, onde faz um nó; abaixo dele, a folga de tensão (laço frouxo) e o
 * nó do fusível, um cordelete que prende a corda ao mesmo mosquetão do
 * primário. Quem está pendurado fica na ponta.
 */
function criarAncoragemBasica(): CenaTabletop {
  const parede = peca('barranco', 'parede', 'Parede de 8 m', [0, 0, 0], { parametros: PAREDE_ANCORAGEM, rotacao: [0, Math.PI, 0] })
  const zFace = pontoNoMundo(parede, 'borda-superior')[2]
  // a placa rente à parede: o +Y local da peça sai da parede (para +Z)
  const naParede = [Math.PI / 2, 0, 0] as Vetor
  const frente = (recuo: number) => zFace + recuo
  return {
    versao: 2,
    nome: 'Ancoragem básica: ponto primário e secundário',
    historiaInicial:
      'Acesso por corda numa parede de 8 m. A corda nasce no ponto secundário, desce em catenária até o ponto primário e faz um nó; abaixo dele ficam a folga de tensão e o fusível. Selecione a corda de trabalho para estudar o que segura a carga e quanto o trabalhador desce se o fusível romper ou se o ponto primário falhar. Mude a folga dos trechos no painel de cordas e compare.',
    objetos: [
      parede,
      peca('pontoAncoragem', 'ponto-secundario', 'Ponto secundário', [-1.4, 7.1, zFace], { rotacao: naParede }),
      peca('pontoAncoragem', 'ponto-primario', 'Ponto primário', [0, 6.1, zFace], { rotacao: naParede }),
      peca('noOito', 'no-fusivel', 'Nó do fusível', [0.3, 5.35, frente(0.12)], { cor: '#7c4a2d' }),
      // pendurado de frente para a parede, com o engate abaixo do primário
      // sentado na cadeirinha, de frente para a parede, preso pelo engate ventral a 3,75 m
      peca('trabalhador', 'trabalhador', 'Trabalhador pendurado na corda', [0.45, 3.75 - (1.0 + PELVE_SENTADO_M - PELVE_EM_PE_M), frente(0.45)], {
        rotacao: [0, Math.PI, 0],
        parametros: { sentadoNaCadeirinha: 1 },
      }),
    ],
    cordas: [
      {
        ...corda('corda-de-trabalho', 'seguranca', 'Corda de trabalho', [
          ponto('ponto-secundario', 'olhal'),
          ponto('ponto-primario', 'olhal'),
          ponto('no-fusivel', 'passagem'),
          ponto('trabalhador', 'engate-ventral'),
        ]),
        // catenária e folga de tensão
        folgas: [0.9, 0.45],
      },
      corda('fusivel', 'fita', 'Fusível (cordelete)', [ponto('ponto-primario', 'olhal'), ponto('no-fusivel', 'passagem')]),
    ],
  }
}

/**
 * Ancoragem em árvores sobre um barranco de 4 m: duas fitas por envolvimento
 * (cesta numa árvore, três voltas e duas pegadas na outra) formam um V até a
 * placa de ancoragem; da placa saem a tração (até a maca, embaixo) e, de um
 * poste com fita enforcada, a corda de segurança. O V começa pré-equalizado e
 * com a carga desviada, para comparar com o autoequalizável.
 */
/** Gira a montagem 180° em volta do eixo vertical: a câmera inicial olha de +Z, e o vão tem de ficar de frente para ela. */
const girarMeiaVolta = (o: ObjetoTabletop): ObjetoTabletop => {
  const [x, y, z] = o.transformacao.posicao
  const [rx, ry, rz] = o.transformacao.rotacao
  return { ...o, transformacao: { ...o.transformacao, posicao: [0 - x, y, 0 - z], rotacao: [rx, ry + Math.PI, rz] } }
}

function criarAncoragensEmArvores(): CenaTabletop {
  const barranco = peca('barranco', 'barranco', 'Barranco de 4 m', [0, 0, 0], {
    parametros: { alturaM: 4, anguloGraus: 75, larguraM: 16, topoM: 10, pistaNoTopo: 0 },
  })
  const y = 4
  const zBorda = pontoNoMundo(barranco, 'borda-superior')[2]
  const zPe = pontoNoMundo(barranco, 'pe')[2]
  const zArvores = zBorda + 6.5
  const arvoreA = peca('arvore', 'arvore-a', 'Árvore A', [-2.2, y, zArvores])
  const arvoreB = peca('arvore', 'arvore-b', 'Árvore B', [2, y, zArvores + 0.6])
  const poste = peca('poste', 'poste', 'Poste', [4.6, y, zArvores - 0.4])
  // o ponto central do V: 3 m à frente das árvores, na direção da borda
  const centro: Vetor = [0, y + 0.95, zArvores - 3]
  /** A fita em volta do tronco, com o mosquetão virado para `alvo`. */
  const fita = (id: string, nome: string, arvore: ObjetoTabletop, alvo: Vetor, tipoEnvolvimento: number): ObjetoTabletop => {
    const novo = novoEnvolvimentoEm(arvore)!
    const [x, yy, z] = novo.transformacao!.posicao
    return peca('envolvimento', id, nome, [x, yy, z], {
      rotacao: [0, Math.atan2(alvo[0] - x, alvo[2] - z), 0],
      parametros: { ...novo.parametros, tipoEnvolvimento, distanciaM: 0.7 },
    })
  }
  const placa = peca('placaAncoragem', 'placa', 'Placa de ancoragem', [centro[0], centro[1] - GEOMETRIA_PLACA_ANCORAGEM.furoDeCima[1], centro[2]])
  return {
    versao: 2,
    nome: 'Ancoragens: placa, envolvimento e equalização',
    historiaInicial:
      'Resgate de uma maca ao pé de um barranco de 4 m. Em cima, duas árvores com fita por envolvimento (cesta na A, três voltas e duas pegadas na B) formam um V até a placa de ancoragem; da placa sai a tração, e a corda de segurança vai a um poste com fita enforcada. Selecione a ancoragem em V (em Cordas) e troque entre autoequalizável e pré-equalizada, mexendo no desvio da carga; selecione cada fita para ver o ângulo e a carga nas pernas.',
    objetos: [
      barranco,
      arvoreA,
      arvoreB,
      poste,
      fita('fita-a', 'Fita em cesta (Árvore A)', arvoreA, centro, 0),
      fita('fita-b', 'Fita em três voltas e duas pegadas (Árvore B)', arvoreB, centro, 2),
      fita('fita-poste', 'Fita enforcada (Poste)', poste, [0, y, zBorda], 1),
      placa,
      // um protetor de corda para cada corda, na borda: a metade de cima deita no topo, a outra desce a face
      peca('protetorCorda', 'protetor-tracao', 'Protetor da tração', [-0.25, y, zBorda]),
      peca('protetorCorda', 'protetor-seguranca', 'Protetor da segurança', [0.35, y, zBorda]),
      peca('maca', 'maca', 'Maca', [0.4, 0, zPe - 2.2]),
    ].map(girarMeiaVolta),
    cordas: [
      corda('tracao', 'tracao', 'Tração (da placa à maca)', [ponto('placa', 'furo-2'), ponto('protetor-tracao', 'passagem'), ponto('maca', 'olhal')], 'paraInicio'),
      corda('seguranca', 'seguranca', 'Segurança (do poste à maca)', [
        ponto('fita-poste', 'mestre'),
        ponto('protetor-seguranca', 'passagem'),
        ponto('maca', 'alca-cabeca'),
      ]),
    ],
    ancoragens: [
      {
        id: 'v-arvores',
        tipo: 'V',
        fixacoes: [ponto('fita-a', 'mestre'), ponto('fita-b', 'mestre')],
        centro: ponto('placa', 'topo'),
        cargaKgf: 200,
        rotulo: 'V nas árvores',
        equalizacao: 'pre-equalizada',
        desvioCargaGraus: 20,
      },
    ],
  }
}

export const PRESETS_SALVAMENTO: readonly PredefinicaoSalvamento[] = [
  {
    id: 'salvamento-escada-45',
    titulo: 'Técnica escada 45° sobre o barranco',
    descricao:
      'Escada a 45° sobre a borda, com o pé junto à traseira da viatura. Duas fitas em V, duas estabilizações até a viatura e içamento da maca por polias e bloqueadores ancorados num toco.',
    referencia: 'tecnica-escada-45.jpg',
    pontosParaValidar: [
      'A viatura está de ré para a borda; na foto a posição exata da viatura em relação à escada não aparece inteira.',
      'O sistema de vantagem mecânica tem dois bloqueadores em linha, sem as polias intermediárias da foto.',
      'A fita em V do topo e a do meio têm o vértice na polia e no mosquetão; confira se é assim na técnica ensinada.',
      'A seta de puxada está na corda de vantagem mecânica; na foto há setas para baixo e uma seta para cima na maca.',
    ],
    criarCena: criarEscada45,
  },
  {
    id: 'salvamento-tirolesa-invertida',
    titulo: 'Técnica tirolesa invertida',
    descricao:
      'Escada vertical na traseira da viatura. Dois Vs de anéis de fita nos banzos, unidos por mosquetão: um prende a tirolesa guia (verde) e o outro a polia do içamento (amarelo). Estabilização azul em três pontos.',
    referencia: 'tecnica-tirolesa-invertida.jpg',
    pontosParaValidar: [
      'A tirolesa guia termina num toco no fundo do barranco; a foto mostra a linha diagonal, sem dizer onde ela se ancora.',
      'A maca fica ligada ao carrinho pela "linha da vítima", cuja cor (rosa) ainda não foi confirmada.',
      'O içamento sai do carrinho, passa pela polia da escada e segue para dois bloqueadores ao lado da viatura.',
      'As três estabilizações vão até pontos da viatura (topo, topo do outro lado e degrau traseiro); a foto não indica quais.',
    ],
    criarCena: criarTirolesaInvertida,
  },
  {
    id: 'salvamento-skate-block',
    titulo: 'Técnica skate block (teleférico)',
    descricao:
      'Teleférico de uma corda só: ela sobe pela guia, onde corre o skate block com a maca, contorna a polia do topo, presa a um poste, e desce até o nó oito no mosquetão do skate block. O operador lá embaixo puxa a ponta livre para subir a maca e a solta para descer.',
    referencia: 'tecnica-skate-block.jpg',
    pontosParaValidar: [
      'A foto mostra uma corda só: guia, retorno com nó oito e a ponta livre na mão do operador. O preset a monta como uma corda de tração, que passa duas vezes pela polia do topo e pelo skate block; confira se é essa a leitura.',
      'A ancoragem do topo é um poste (a foto só desenha a âncora): fita do poste, mosquetão e a polia pendurada. No fundo, um toco com fita e a polia do pé. Troque por qualquer ponto que a aula use.',
      'O skate block está a 42% da guia, a partir do topo, e a maca pende 60 cm sob ele, deitada ao longo da guia; a foto não marca a posição.',
      'A vantagem mecânica é 1:1: quem puxa a ponta livre sustenta a maca. A foto não mostra freio nem bloqueador no fundo, nem corda de segurança, por isso o aviso de içamento sem segurança aparece.',
      'A seta de puxada fica na ponta livre, na polia do pé. A seta tracejada para baixo da foto (a queda da carga) não foi desenhada.',
    ],
    criarCena: criarSkateBlock,
  },
  {
    id: 'salvamento-canal-ponte',
    titulo: 'Canal e ponte (tipo Arrudas)',
    descricao:
      'Canal de concreto com banqueta, mureta e água, duas pontes baixas e um carro capotado sob a primeira. A viatura está rente à mureta e a escada a 45° faz de mastro para içar a maca da banqueta.',
    referencia: 'arrudas-1-canal-visto-da-mureta.jpg, arrudas-2-carro-capotado.jpg, arrudas-3-escada-na-mureta.jpg',
    pontosParaValidar: [
      'As medidas do canal são didáticas (paredes de 5 m, leito de 10 m, banqueta de 1,5 m a 1,2 m, mureta de 1 m, vão de 11,5 m): as fotos não dão as medidas reais. Ajuste no inspetor do canal.',
      'O nível da água (0,5 m) e as setas de correnteza são só desenho; a água não tem física.',
      'A margem oposta é uma parede; numa das fotos ela é uma rampa de concreto (a inclinação da margem se ajusta de 30° a 90°).',
      'A escada e as fitas em V são as da técnica da escada a 45°, aplicadas à banqueta; o içamento vai a um sistema de vantagem 3:1 em Z (a foto mostra um conjunto de bloqueadores). Confira com a foto da escada na mureta.',
    ],
    criarCena: criarCanalEPonte,
  },
  {
    id: 'salvamento-cisterna',
    titulo: 'Cisterna: resgate vertical',
    descricao:
      'Vítima na maca no fundo de uma cisterna de 6 m. A polia fica na boca, presa por uma ancoragem em V a dois tocos; o içamento vai a um sistema de vantagem 5:1 e uma corda de segurança desce pela borda.',
    referencia: 'sem foto de referência: montagem didática',
    pontosParaValidar: [
      'Não há foto de referência: a montagem é uma proposta para o instrutor corrigir.',
      'O catálogo não tem tripé nem talabarte (pergunta em aberto do plano): a polia é presa por uma ancoragem em V a dois tocos.',
      'A cisterna é escavada no mesmo plano da rua, com paredes translúcidas para ver o interior; a tampa existe como opção, e a escada de marinheiro ainda não foi definida.',
      'O diâmetro (2,4 m) e a profundidade (6 m) são didáticos.',
    ],
    criarCena: criarCisterna,
  },
  {
    id: 'salvamento-trabalho-em-altura',
    titulo: 'Trabalho em altura: fator de queda',
    descricao:
      'Dois trabalhadores na borda de uma laje de 4,8 m: um preso a uma torre, acima do engate; o outro a um toco, no nível dos pés. Mostra o fator de queda, a zona livre de queda e, na queda animada, o balanço contra a parede.',
    referencia: 'sem foto de referência: montagem didática',
    pontosParaValidar: [
      'Todos os valores de referência são provisórios: talabarte de 1,8 m, absorvedor de 1 m, engate a 1,45 m dos pés e margem de 1 m. O instrutor define os números e confere na NR-35 e nas normas dos equipamentos.',
      'A zona livre de queda e o fator de queda são os da queda na vertical da ancoragem. A queda animada acrescenta o pêndulo, mas é cinemática (sem motor de física) e só o terreno conta como estrutura: a torre não bloqueia o balanço.',
      'As duas ancoragens ficam a mais de 1 m da borda, então nos dois casos o balanço bate na parede da laje; prender na vertical da borda elimina o impacto.',
      'A superfície abaixo é o primeiro obstáculo de onde o trabalhador pode cair, dentro do alcance do talabarte: aqui, o chão além da borda da laje.',
      'O talabarte é desenhado em linha reta entre o engate e a ancoragem; o comprimento usado no cálculo é o do parâmetro do trabalhador.',
    ],
    criarCena: criarTrabalhoEmAltura,
  },
  {
    id: 'salvamento-trabalho-na-torre',
    titulo: 'Trabalho dentro da torre: o patamar de baixo',
    descricao:
      'Dois trabalhadores no patamar de 6 m de uma torre de 12 m. Um preso no canto oposto ao alçapão, outro junto do alçapão com talabarte longo: dentro da torre, o patamar de baixo é o obstáculo da queda.',
    referencia: 'sem foto de referência: montagem didática',
    pontosParaValidar: [
      'A torre (12 m × 3 m, patamares a cada 3 m, alçapão de 0,8 m num canto) é genérica: o instrutor ajusta as medidas à estrutura da aula.',
      'As ancoragens de cada patamar ficam a 2 m do piso, nos montantes; confira se é assim na estrutura real.',
      'O trabalhador B usa talabarte de 2 m e absorvedor de 1,75 m de propósito, para mostrar que a zona livre fica limitada pelo espaço entre patamares.',
      'Os valores de referência continuam provisórios (ver o preset de trabalho em altura).',
    ],
    criarCena: criarTrabalhoDentroDaTorre,
  },
  {
    id: 'salvamento-ancoragem-basica',
    titulo: 'Ancoragem básica: ponto primário e secundário',
    descricao:
      'Acesso por corda numa parede: ponto secundário, catenária, ponto primário, folga de tensão e fusível, como na figura. Selecione a corda de trabalho para ver o que segura a carga e quanto o trabalhador desce se o fusível romper ou se o primário falhar.',
    referencia: 'ancoragem-basica.jpg',
    pontosParaValidar: [
      'A leitura da figura: o fusível é um cordelete do mosquetão do primário até a corda, abaixo do nó, e segura a carga no uso normal; se ele romper, a folga de tensão estica e o nó do primário assume. Confirmar com o instrutor.',
      'As folgas (0,90 m na catenária e 0,45 m na folga de tensão) são de exemplo; o instrutor define quanto deixar em cada uma.',
      'O cálculo da queda conta só o comprimento de corda: sem elasticidade, sem força de impacto e com a pessoa parando na vertical do ponto que sobrou.',
      'Os pontos de ancoragem são chapeletas genéricas com argola; a resistência de cada ponto não entra na conta.',
      'O trabalhador aparece sentado na cadeirinha, preso pelo engate ventral; confira se a postura e o engate batem com a técnica ensinada.',
    ],
    criarCena: criarAncoragemBasica,
  },
  {
    id: 'salvamento-ancoragens-arvores',
    titulo: 'Ancoragens: placa, envolvimento e equalização',
    descricao:
      'Duas árvores com fita por envolvimento (cesta e três voltas) num V até a placa de ancoragem, e um poste com fita enforcada para a segurança. Compare a ancoragem pré-equalizada com a autoequalizável e o ângulo das fitas.',
    referencia: 'sem foto de referência: montagem didática',
    pontosParaValidar: [
      'A geometria das fitas (a perna sai tangente ao tronco) e a carga por perna da cesta, igual à de um V, são um modelo ideal, sem atrito.',
      'O enforcado diminui a resistência da fita; o app não dá número, porque o valor é do fabricante.',
      'A pré-equalizada é calculada com as pernas como molas do mesmo material (a mais curta é a mais dura); o instrutor confere se é essa a ideia que quer passar.',
      'Desvio de 20° no V para começar mostrando uma perna mais carregada; com 0° e duas pernas, as duas equalizações dão o mesmo.',
    ],
    criarCena: criarAncoragensEmArvores,
  },
]

export const obterPresetSalvamentoPorId = (id: string): PredefinicaoSalvamento | undefined =>
  PRESETS_SALVAMENTO.find((p) => p.id === id)

/** Uma cópia nova da cena do preset, para o instrutor editar sem alterar o original. */
export const criarCenaPresetSalvamento = (id: string): CenaTabletop | undefined => {
  const preset = obterPresetSalvamentoPorId(id)
  return preset ? copiarCenaTabletop(preset.criarCena()) : undefined
}

/**
 * O cenário com que o Table top começa e para o qual "Redefinir tudo" volta:
 * a estrada no topo do barranco, a viatura de ré para a borda e a encosta com
 * árvores. É a base das técnicas de salvamento, sem nenhuma corda montada.
 */
export const criarCenaPadrao = (): CenaTabletop => ({
  versao: 2,
  nome: 'Cenário padrão: estrada sobre o barranco',
  historiaInicial:
    'A estrada fica no topo de um barranco de 4 m, com a viatura de ré para a borda; embaixo, a encosta de mato com árvores. Monte a técnica que quiser ou abra uma das técnicas de salvamento.',
  chao: 'mato',
  objetos: cenarioBase(),
})
